import Page from "./Page";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  setActiveTask,
  updateTask as updateTaskAction,
} from "../redux/actions/tasks";
import { Container, Grid, Typography } from "@mui/material";
import Button from "../components/input/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import TaskDataContainer from "../components/tasks/TaskDataContainer";
import HelpLink from "../components/help/HelpLink";
import scheduleNotificationsForTask from "../notifications/scheduleNotificationsForTask";
import removeNotificationsForTask from "../notifications/removeNotificationsForTask";
import { Capacitor } from "@capacitor/core";
import useTaskAPIClient from "../api/task/hooks/useTaskAPIClient";
import * as jsonpatch from "fast-json-patch";
import {
  validateTask,
  TASK_VALIDATION_LIMITS,
  parseBackendValidationErrors,
} from "../helpers/taskValidation";

const defaultAttributes = {
  difficulty: 5,
  importance: 5,
  fun: 5,
};

const CreateTaskPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPage = searchParams.get("from");
  const noteInputValueRef = useRef();
  const [saving, setSaving] = useState(false);
  const { updateTask } = useTaskAPIClient();

  const activeTaskId = useSelector((state) => state.tasks.activeTask);
  const activeTask = useSelector((state) => state.tasks.taskSet[activeTaskId]);

  useEffect(() => {
    if (!activeTaskId || !activeTask) {
      const returnPath =
        fromPage === "planner"
          ? "/planner"
          : fromPage === "dashboard"
            ? "/dashboard"
            : "/tasks";
      navigate(returnPath);
    }
  }, [activeTaskId, activeTask, navigate, fromPage]);

  const [name, setName] = useState(activeTask ? activeTask.name : "");
  const [nameError, setNameError] = useState("");
  const [notes, setNotes] = useState(activeTask ? activeTask.notes : {});
  const [noteInputError, setNoteInputError] = useState("");
  const [emoji, setEmoji] = useState(activeTask ? activeTask.emoji : null);
  const [targeted, setTargeted] = useState(
    activeTask ? activeTask.targeted : false,
  );
  const [labels, setLabels] = useState(
    activeTask && activeTask.labels ? activeTask.labels : [],
  );
  const [attributes, setAttributes] = useState(
    activeTask ? activeTask.attributes : defaultAttributes,
  );
  const [hasDeadline, setHasDeadline] = useState(
    activeTask && activeTask.deadline,
  );
  const [deadline, setDeadline] = useState(
    activeTask && activeTask.deadline
      ? dayjs.unix(activeTask.deadline)
      : undefined,
  );
  const [isRecurring, setRecurring] = useState(
    activeTask && !!activeTask.frequency,
  );
  const [frequency, setFrequency] = useState(
    activeTask && activeTask.frequency ? activeTask.frequency : undefined,
  );
  const [schedule, setSchedule] = useState(
    activeTask && activeTask.schedule
      ? dayjs.unix(activeTask.schedule)
      : undefined,
  );

  const [showEditLabelsPanel, setShowEditLabelsPanel] = useState(false);

  const onAttributeChange = (attribute) => {
    return (incrementer) => {
      const newAttributes = {
        ...attributes,
        [attribute]: attributes[attribute] + incrementer,
      };

      setAttributes(newAttributes);
    };
  };

  const onNameChange = (event) => {
    const newName = event.target.value;
    setName(newName);
    // Clear error if name is now valid, or show max length error
    if (newName.trim()) {
      if (newName.length > TASK_VALIDATION_LIMITS.NAME_MAX_LENGTH) {
        setNameError(
          `Task name must be ${TASK_VALIDATION_LIMITS.NAME_MAX_LENGTH} characters or less`,
        );
      } else {
        setNameError("");
      }
    }
  };

  const onChangeEditLabelPanelVisibility = (visibility) => {
    setShowEditLabelsPanel(visibility);
  };

  const onLabelsChange = (newLabels) => {
    setLabels(newLabels);
  };

  const onLabelRemoved = (labelId) => {
    const newLabels = [...labels];
    const labelIndex = newLabels.indexOf(labelId);
    if (labelIndex > -1) {
      newLabels.splice(labelIndex, 1);
    }

    setLabels(newLabels);
  };

  const onAddNote = (noteText) => {
    if (noteText !== "") {
      const newNotes = { ...notes };
      const newNoteId = +new Date();
      newNotes[newNoteId] = {
        text: noteText,
        id: newNoteId,
        completed: false,
      };

      setNotes(newNotes);
    }
  };

  const onNoteStatusChange = ({ id, status }) => {
    const newNotes = { ...notes };
    const newNote = newNotes[id];
    newNote.completed = status;

    setNotes(newNotes);
  };

  const onNoteDeleted = (id) => {
    const newNotes = { ...notes };
    delete newNotes[id];

    setNotes(newNotes);
  };

  const getReturnPath = () => {
    if (fromPage === "planner") {
      return "/planner";
    }
    if (fromPage === "dashboard") {
      return "/dashboard";
    }
    return "/tasks"; // default to tasks page
  };

  const onCancelClick = () => {
    navigate(getReturnPath());
    dispatch(setActiveTask(null));
  };

  const onAcceptClick = async () => {
    const noteInputValue = noteInputValueRef.current?.getNoteValue() || "";

    // Build the new notes object including any unsaved note
    const newNotes = { ...notes };
    if (noteInputValue) {
      const newNoteId = +new Date();
      newNotes[newNoteId] = {
        text: noteInputValue,
        id: newNoteId,
        completed: false,
      };
    }

    const newTask = {
      ...activeTask,
      taskId: activeTaskId,
      name,
      notes: newNotes,
      attributes,
      labels,
      targeted,
      deadline: deadline?.unix() ?? undefined,
      frequency: frequency ?? undefined,
      emoji,
      schedule: schedule?.unix() ?? undefined,
    };

    // Validate the task before submission
    const validation = validateTask(newTask, "");

    if (!validation.isValid) {
      if (validation.errors.name) {
        setNameError(validation.errors.name);
      }
      if (validation.errors.unsavedNote || validation.errors.notesCount) {
        setNoteInputError(
          validation.errors.unsavedNote || validation.errors.notesCount,
        );
      }
      return;
    }

    setSaving(true);

    const patches = jsonpatch.compare(activeTask, newTask);

    try {
      await updateTask({
        taskId: activeTaskId,
        patches,
      });
      dispatch(
        updateTaskAction(activeTaskId, {
          ...activeTask,
          ...newTask,
        }),
      );
      dispatch(setActiveTask(null));

      // If deadline, schedule, or name has changed, remove existing notifications and schedule new ones
      if (Capacitor.isNativePlatform()) {
        const deadlineChanged = activeTask.deadline !== newTask.deadline;
        const scheduleChanged = activeTask.schedule !== newTask.schedule;
        const nameChanged = activeTask.name !== newTask.name;

        if (deadlineChanged || scheduleChanged || nameChanged) {
          console.log(
            "Removing existing notifications and scheduling new ones",
          );
          await removeNotificationsForTask(activeTaskId);
          await scheduleNotificationsForTask(activeTaskId);
        }
      }

      setSaving(false);
      navigate(getReturnPath());
    } catch (error) {
      setSaving(false);
      // Handle validation errors from backend
      if (error.status === 400 && error.errors) {
        const parsedErrors = parseBackendValidationErrors(error.errors);
        if (parsedErrors.name) {
          setNameError(parsedErrors.name);
        }
        if (parsedErrors.unsavedNote || parsedErrors.notesCount) {
          setNoteInputError(
            parsedErrors.unsavedNote || parsedErrors.notesCount,
          );
        }
      } else {
        // Re-throw for other error handling
        throw error;
      }
    }
  };

  return (
    <Page className="edit-task-page">
      <Grid item xs={12}>
        <div className="edit-task__header">
          <Typography
            variant="h2WhiteNoMargin"
            className="edit-task__header-label"
          >
            Edit Task
          </Typography>
          <HelpLink
            to="/about/features/tasks/create-a-task"
            color="white"
            size="large"
            sx={{ position: "absolute", right: 8 }}
          />
        </div>
      </Grid>
      <Container
        sx={{
          maxWidth: 800,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <TaskDataContainer
          name={name}
          onNameChange={onNameChange}
          nameError={!!nameError}
          nameHelperText={nameError}
          labels={labels}
          onLabelsChange={onLabelsChange}
          onLabelRemoved={onLabelRemoved}
          emoji={emoji}
          setEmoji={setEmoji}
          deadline={deadline}
          setHasDeadline={setHasDeadline}
          hasDeadline={hasDeadline}
          setDeadline={setDeadline}
          isRecurring={isRecurring}
          setRecurring={setRecurring}
          frequency={frequency}
          setFrequency={setFrequency}
          schedule={schedule}
          setSchedule={setSchedule}
          targeted={targeted}
          setTargeted={setTargeted}
          attributes={attributes}
          onAttributeChange={onAttributeChange}
          notes={notes}
          onAddNote={onAddNote}
          onNoteStatusChange={onNoteStatusChange}
          onNoteDeleted={onNoteDeleted}
          showEditLabelsPanel={showEditLabelsPanel}
          onChangeEditLabelPanelVisibility={onChangeEditLabelPanelVisibility}
          noteInputValueRef={noteInputValueRef}
          noteInputError={noteInputError}
          onNoteInputErrorChange={setNoteInputError}
        />
        {!showEditLabelsPanel && (
          <Grid xs={12} md={6} className="edit-task__footer">
            <Button
              text="Cancel"
              color="red"
              size="large"
              onClick={onCancelClick}
            />
            <Button
              text="Accept"
              color="green"
              size="large"
              onClick={onAcceptClick}
              loading={saving}
            />
          </Grid>
        )}
      </Container>
    </Page>
  );
};

export default CreateTaskPage;
